// src/hooks/useOnlineStatus.js
import { useEffect, useState } from 'react';
import { doc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { watchUserProfile } from './userProfiles';

export function useOnlineStatus() {
  useEffect(() => {
    let unsubscribeAuth;

    const setupOnlineStatus = (user) => {
      if (!user) return;

      const userRef = doc(db, 'users', user.uid);

      const setOnlineStatus = async (isOnline) => {
        try {
          // Only update fields, create if missing
          await updateDoc(userRef, {
            isOnline,
            lastActive: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error updating online status:', error);
        }
      };

      // Set user online
      setOnlineStatus(true);

      const handleBeforeUnload = () => {
        setOnlineStatus(false).catch(() => {});
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      // Cleanup on unmount / logout
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        setOnlineStatus(false).catch(() => {});
      };
    };

    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      const cleanup = setupOnlineStatus(user);
      return () => {
        if (cleanup) cleanup();
      };
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []);
}


export function useUserOnlineStatus(userId) {
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    if (!userId) return;

    // Chat members can be in an organisation we hold no binding to, and this
    // listener had no error callback — a denied document surfaced as an uncaught
    // SDK error and the dot stayed dark for ever. watchUserProfile resolves such
    // a user once through getUserProfiles instead.
    return watchUserProfile(userId, (user) => {
      setIsOnline(user?.isOnline || false);
    });
  }, [userId]);

  return isOnline;
}